<template>
  <div class="execution-detail">
    <a-page-header
      title="执行详情"
      @back="goBack"
    >
      <template #extra>
        <a-space>
          <a-button v-if="!result?.score_level" @click="autoScore">自动评分</a-button>
          <a-button v-if="!result?.score_level" @click="manualScore">人工评分</a-button>
          <a-button @click="viewComparison">对比分析</a-button>
          <a-button type="primary" @click="reExecute">重新执行</a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <a-row :gutter="16" style="margin-top: 16px" v-if="result">
        <!-- 基本信息卡片 -->
        <a-col :span="24">
          <a-card title="基本信息">
            <a-descriptions :column="3" bordered>
              <a-descriptions-item label="测试用例">
                <a @click="viewTestCase(result.test_case_id)">
                  {{ result.case_name }}
                </a>
              </a-descriptions-item>
              <a-descriptions-item label="配置快照">
                {{ result.snapshot_name }}
              </a-descriptions-item>
              <a-descriptions-item label="测试计划">
                <a @click="viewTestPlan(result.test_plan_id)">
                  {{ result.plan_name || '-' }}
                </a>
              </a-descriptions-item>
              <a-descriptions-item label="执行时间">
                {{ result.execution_time }}
              </a-descriptions-item>
              <a-descriptions-item label="执行状态">
                <a-tag :color="getStatusColor(result.execution_status)">
                  {{ getStatusText(result.execution_status) }}
                </a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="执行耗时">
                {{ result.execution_duration }}ms
              </a-descriptions-item>
            </a-descriptions>
          </a-card>
        </a-col>

        <!-- 评分信息 -->
        <a-col :span="24" style="margin-top: 16px">
          <a-card title="评分信息">
            <a-empty v-if="!result.score_level" description="尚未评分">
              <a-space>
                <a-button type="primary" @click="autoScore">自动评分</a-button>
                <a-button @click="manualScore">人工评分</a-button>
              </a-space>
            </a-empty>
            <div v-else>
              <a-descriptions :column="2" bordered>
                <a-descriptions-item label="评分等级">
                  <a-tag :color="getScoreLevelColor(result.score_level)" style="font-size: 16px">
                    {{ result.score_level }}级
                  </a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="评分值">
                  <a-statistic 
                    :value="result.score_value" 
                    suffix="分"
                    :value-style="{ color: getScoreColor(result.score_value) }"
                  />
                </a-descriptions-item>
                <a-descriptions-item label="是否通过">
                  <a-tag :color="result.is_passed ? 'success' : 'error'" style="font-size: 14px">
                    {{ result.is_passed ? '✓ 通过' : '✗ 未通过' }}
                  </a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="评分方式">
                  {{ result.scored_by === 'auto' ? '自动评分' : '人工评分' }}
                </a-descriptions-item>
                <a-descriptions-item label="评分理由" :span="2">
                  <div class="score-reason">{{ result.score_reason || '-' }}</div>
                </a-descriptions-item>
              </a-descriptions>

              <!-- 关键差异 -->
              <a-divider orientation="left">关键差异</a-divider>
              <a-list
                v-if="result.key_differences && result.key_differences.length > 0"
                :data-source="result.key_differences"
                size="small"
                bordered
              >
                <template #renderItem="{ item, index }">
                  <a-list-item>
                    <a-badge :count="index + 1" :number-style="{ backgroundColor: '#52c41a' }" />
                    <span style="margin-left: 12px">{{ item }}</span>
                  </a-list-item>
                </template>
              </a-list>
              <a-empty v-else description="无差异记录" />

              <!-- 改进建议 -->
              <a-divider orientation="left">改进建议</a-divider>
              <a-list
                v-if="result.suggestions && result.suggestions.length > 0"
                :data-source="result.suggestions"
                size="small"
                bordered
              >
                <template #renderItem="{ item, index }">
                  <a-list-item>
                    <a-badge :count="index + 1" :number-style="{ backgroundColor: '#1890ff' }" />
                    <span style="margin-left: 12px">{{ item }}</span>
                  </a-list-item>
                </template>
              </a-list>
              <a-empty v-else description="无改进建议" />
            </div>
          </a-card>
        </a-col>

        <!-- Token统计 -->
        <a-col :span="24" style="margin-top: 16px">
          <a-card title="Token统计">
            <a-row :gutter="16">
              <a-col :span="8">
                <a-statistic 
                  title="输入Token" 
                  :value="result.input_tokens" 
                  suffix="tokens"
                />
              </a-col>
              <a-col :span="8">
                <a-statistic 
                  title="输出Token" 
                  :value="result.output_tokens" 
                  suffix="tokens"
                />
              </a-col>
              <a-col :span="8">
                <a-statistic 
                  title="总Token" 
                  :value="(result.input_tokens || 0) + (result.output_tokens || 0)" 
                  suffix="tokens"
                />
              </a-col>
            </a-row>
          </a-card>
        </a-col>

        <!-- 数据对比 -->
        <a-col :span="24" style="margin-top: 16px">
          <a-card title="数据对比">
            <a-tabs>
              <a-tab-pane key="input" tab="输入数据">
                <a-card size="small">
                  <pre class="json-display">{{ formatJSON(result.input_data) }}</pre>
                </a-card>
              </a-tab-pane>
              <a-tab-pane key="expected" tab="预期输出">
                <a-card size="small">
                  <template v-if="result.expected_output?.steps && Array.isArray(result.expected_output.steps)">
                    <a-collapse>
                      <a-collapse-panel v-for="(s, idx) in result.expected_output.steps" :key="idx" :header="`步骤 ${s.step_order}`">
                        <pre class="json-display">{{ formatJSON(s.response_data || s.output || s) }}</pre>
                      </a-collapse-panel>
                    </a-collapse>
                  </template>
                  <pre v-else class="json-display">{{ formatJSON(result.expected_output) }}</pre>
                </a-card>
              </a-tab-pane>
              <a-tab-pane key="actual" tab="实际输出">
                <a-card size="small">
                  <template v-if="result.actual_output?.steps && Array.isArray(result.actual_output.steps)">
                    <a-collapse>
                      <a-collapse-panel v-for="(s, idx) in result.actual_output.steps" :key="idx" :header="`步骤 ${s.step_order}`">
                        <pre class="json-display">{{ formatJSON(s.output || s) }}</pre>
                      </a-collapse-panel>
                    </a-collapse>
                  </template>
                  <pre v-else class="json-display">{{ formatJSON(result.actual_output) }}</pre>
                </a-card>
              </a-tab-pane>
              <a-tab-pane key="diff" tab="差异对比">
                <a-card size="small">
                  <div class="diff-view">
                    <a-alert
                      message="差异高亮显示"
                      description="红色表示预期但未出现，绿色表示额外出现的内容"
                      type="info"
                      show-icon
                      style="margin-bottom: 16px"
                    />
                    <a-row :gutter="16">
                      <a-col :span="12">
                        <h4>预期输出</h4>
                        <template v-if="result.expected_output?.steps && Array.isArray(result.expected_output.steps)">
                          <a-collapse>
                            <a-collapse-panel v-for="(s, idx) in result.expected_output.steps" :key="idx" :header="`步骤 ${s.step_order}`">
                              <pre class="json-display diff-expected">{{ formatJSON(s.response_data || s.output || s) }}</pre>
                            </a-collapse-panel>
                          </a-collapse>
                        </template>
                        <pre v-else class="json-display diff-expected">{{ formatJSON(result.expected_output) }}</pre>
                      </a-col>
                      <a-col :span="12">
                        <h4>实际输出</h4>
                        <template v-if="result.actual_output?.steps && Array.isArray(result.actual_output.steps)">
                          <a-collapse>
                            <a-collapse-panel v-for="(s, idx) in result.actual_output.steps" :key="idx" :header="`步骤 ${s.step_order}`">
                              <pre class="json-display diff-actual">{{ formatJSON(s.output || s) }}</pre>
                            </a-collapse-panel>
                          </a-collapse>
                        </template>
                        <pre v-else class="json-display diff-actual">{{ formatJSON(result.actual_output) }}</pre>
                      </a-col>
                    </a-row>
                  </div>
                </a-card>
              </a-tab-pane>
            </a-tabs>
          </a-card>
        </a-col>

        <!-- 错误信息（如果有） -->
        <a-col :span="24" style="margin-top: 16px" v-if="result.error_message">
          <a-card title="错误信息">
            <a-alert
              type="error"
              :message="result.error_message"
              show-icon
            />
          </a-card>
        </a-col>
      </a-row>
    </a-spin>

    <!-- 人工评分对话框 -->
    <a-modal
      v-model:open="scoreModalVisible"
      title="人工评分"
      @ok="submitManualScore"
      width="700px"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="评分等级" required>
          <a-radio-group v-model:value="scoreForm.score_level" size="large">
            <a-radio-button value="A">A级（优秀）</a-radio-button>
            <a-radio-button value="B">B级（良好）</a-radio-button>
            <a-radio-button value="C">C级（及格）</a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="评分理由">
          <a-textarea 
            v-model:value="scoreForm.score_reason" 
            :rows="4"
            placeholder="请描述评分理由"
          />
        </a-form-item>
        <a-form-item label="关键差异">
          <a-textarea
            v-model:value="scoreForm.key_differences_text"
            :rows="3"
            placeholder="每行一条差异点"
          />
        </a-form-item>
        <a-form-item label="改进建议">
          <a-textarea
            v-model:value="scoreForm.suggestions_text"
            :rows="3"
            placeholder="每行一条建议"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { 
  getResultDetail, 
  autoScoreSingle, 
  manualScore as manualScoreAPI 
} from '@/api/evalExecutionV2';

const route = useRoute();
const router = useRouter();

const resultId = ref(Number(route.params.id));
const result = ref(null);
const loading = ref(false);
const scoreModalVisible = ref(false);

const scoreForm = reactive({
  score_level: 'B',
  score_reason: '',
  key_differences_text: '',
  suggestions_text: ''
});

const fetchDetail = async () => {
  loading.value = true;
  try {
    const res = await getResultDetail(resultId.value);
    if (res.code === 200) {
      result.value = res.data;
    }
  } catch (error) {
    message.error('获取详情失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.back();
};

const autoScore = async () => {
  try {
    message.loading('正在自动评分...', 0);
    await autoScoreSingle(resultId.value);
    message.destroy();
    message.success('自动评分完成');
    fetchDetail();
  } catch (error) {
    message.destroy();
    message.error('自动评分失败：' + error.message);
  }
};

const manualScore = () => {
  scoreForm.score_level = 'B';
  scoreForm.score_reason = '';
  scoreForm.key_differences_text = '';
  scoreForm.suggestions_text = '';
  scoreModalVisible.value = true;
};

const submitManualScore = async () => {
  try {
    const data = {
      score_level: scoreForm.score_level,
      score_reason: scoreForm.score_reason,
      key_differences: scoreForm.key_differences_text.split('\n').filter(s => s.trim()),
      suggestions: scoreForm.suggestions_text.split('\n').filter(s => s.trim()),
      scored_by: 'manual'
    };
    await manualScoreAPI(resultId.value, data);
    message.success('评分成功');
    scoreModalVisible.value = false;
    fetchDetail();
  } catch (error) {
    message.error('评分失败：' + error.message);
  }
};

const viewComparison = () => {
  message.info('对比分析功能开发中...');
};

const reExecute = () => {
  message.info('请通过测试计划重新执行');
  router.push('/eval-v2/test-plans');
};

const viewTestCase = (id) => {
  router.push(`/eval-v2/test-case/${id}`);
};

const viewTestPlan = (id) => {
  router.push(`/eval-v2/test-plans`);
};

const formatJSON = (data) => {
  if (!data) return 'null';
  return JSON.stringify(data, null, 2);
};

const getStatusColor = (status) => {
  const colors = {
    success: 'success',
    failed: 'error',
    timeout: 'warning',
    error: 'error'
  };
  return colors[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    success: '成功',
    failed: '失败',
    timeout: '超时',
    error: '错误'
  };
  return texts[status] || status;
};

const getScoreLevelColor = (level) => {
  const colors = {
    A: 'success',
    B: 'processing',
    C: 'warning'
  };
  return colors[level] || 'default';
};

const getScoreColor = (score) => {
  if (score >= 90) return '#52c41a';
  if (score >= 70) return '#1890ff';
  return '#faad14';
};

onMounted(() => {
  fetchDetail();
});
</script>

<style scoped>
.execution-detail {
  padding: 24px;
}

.score-reason {
  white-space: pre-wrap;
  line-height: 1.6;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
}

.json-display {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  max-height: 500px;
  overflow: auto;
  font-size: 12px;
  font-family: 'Courier New', monospace;
}

.diff-view h4 {
  margin-bottom: 8px;
  color: #666;
}

.diff-expected {
  background: #fff1f0;
  border-left: 3px solid #ff4d4f;
}

.diff-actual {
  background: #f6ffed;
  border-left: 3px solid #52c41a;
}
</style>

